import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { signOutUser } from '../firebase/authService';

const Header = ({ isScrolled }) => {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const isGideonPage = location.pathname === '/gideon-force';
  const navItems = isGideonPage
    ? [
        { route: '/', label: t('navigation.home', 'Home') },
        { route: '/gideon-force', label: t('navigation.gideonForce', 'Gideon Force') }
      ]
    : [
        { id: 'about', label: t('navigation.about') },
        { id: 'prayer-units', label: t('navigation.prayerUnits') },
        { route: '/gideon-force', label: t('navigation.gideonForce', 'Gideon Force') },
        { id: 'features', label: t('navigation.features') },
        { id: 'download', label: t('navigation.download') },
        { id: 'faq', label: t('navigation.faq') },
        { id: 'contact', label: t('navigation.contact') }
      ];

  const handleNavClick = (item) => {
    if (item.route) {
      navigate(item.route);
      setIsMobileMenuOpen(false);
    } else {
      scrollToSection(item.id);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/');
      setIsMobileMenuOpen(false);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const titleColor = isScrolled ? 'text-prayer-red' : 'text-white';
  const subtitleColor = isScrolled ? 'text-gray-500' : 'text-white/70';
  const linkColor = isScrolled ? 'text-gray-700' : 'text-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg/60' : 'bg-transparent'
      }`}
    >
      <div className="container-max">
        <div className="flex flex-wrap items-center gap-4 py-4 px-4 sm:px-6 lg:px-8">
          {/* Brand */}
          <div className="leading-tight">
            <p
              className={`font-bold transition-colors duration-300 ${titleColor}`}
            >
              <span className="hidden md:inline text-lg">
                {t('brand.fullName')}
              </span>
              <span className="md:hidden text-base">{t('brand.shortName')}</span>
            </p>
            <p
              className={`text-xs uppercase tracking-[0.18em] font-semibold transition-colors duration-300 ${subtitleColor}`}
            >
              {t('brand.tagline')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 ml-auto">
            <div className="hidden xl:flex items-center gap-3">
              <LanguageSelector />
              {currentUser ? (
                <>
                  <Link
                    to="/dashboard"
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-prayer-red ${linkColor}`}
                  >
                    {t('navigation.dashboard', 'Dashboard')}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-prayer-red ${linkColor}`}
                  >
                    {t('navigation.signOut', 'Sign Out')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/auth/login"
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-prayer-red ${linkColor}`}
                  >
                    {t('navigation.signIn')}
                  </Link>
                  <Link
                    to="/auth/register"
                    className="inline-flex items-center justify-center h-10 px-4 bg-prayer-red text-white text-sm font-semibold rounded-lg shadow-lg hover:bg-red-600 transition-all duration-200"
                  >
                    {t('navigation.signUp')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg border border-white/30 text-white/90 backdrop-blur"
            >
              <svg
                className={`w-6 h-6 transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="bg-white shadow-xl rounded-b-2xl overflow-hidden">
            <div className="px-4 py-4 space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                <span className="text-sm font-semibold text-gray-600">
                  {t('navigation.language')}
                </span>
                <LanguageSelector />
              </div>
              {navItems.map((item) => (
                <button
                  key={item.id || item.route}
                  onClick={() => handleNavClick(item)}
                  className="block w-full text-left py-2 px-4 text-gray-700 hover:text-prayer-red hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  {item.label}
                </button>
              ))}
              
              {/* Mobile Auth Buttons */}
              <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
                {currentUser ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full text-center py-2 px-4 text-gray-700 hover:text-prayer-red hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      {t('navigation.dashboard', 'Dashboard')}
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-center py-2 px-4 text-gray-700 hover:text-prayer-red hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      {t('navigation.signOut', 'Sign Out')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/auth/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full text-center py-2 px-4 text-gray-700 hover:text-prayer-red hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      {t('navigation.signIn')}
                    </Link>
                    <Link
                      to="/auth/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full text-center py-2 px-4 bg-prayer-red text-white hover:bg-red-700 rounded-lg transition-colors duration-200 font-medium"
                    >
                      {t('navigation.signUp')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 